/**
 *      ▄ █
 *    █ █ ▀▀█  LOADBALANCER CLIENT
 *    █ ▀ ▀ █  @copyright Copyright (c) 2025
 */
import { BaseComponent } from '../base.component';
import { Component, ElementRef, Input, ViewChild } from '@angular/core';
import { ErrorResponseModel } from '../../models';
import { LoadingService, LogService } from '../../services';
import { isObservable } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
    selector: 'app-loading-button',
    templateUrl: './loadingButton.component.html',
    styleUrls: ['./loadingButton.component.scss']
})

export class LoadingButtonComponent extends BaseComponent {
    /**
     * Expose the button element to parent components
     */
    @ViewChild('buttonReference', { read: ElementRef }) public buttonReference: any;

    /**
     * Text on the loading
     */
    @Input() public buttonText: string;

    /**
     * Set the button icon of the embedded loading button.
     */
    @Input() public buttonIconLocation: string = null;

    /**
     * Set the button icon of the embedded loading button.
     */
    @Input() public buttonIconPosition: 'left' | 'right' = 'right';

    /**
     * The loading class
     */
    @Input() public buttonClass: string;

    /**
     * The loading ID
     */
    @Input() public buttonId: string;

    /**
     * Set the loading disabled.
     */
    @Input() public isDisabled: boolean = false;

    /**
     * The type of loading.
     */
    @Input() public buttonType: string = 'button';

    /**
     * Button is embedded in another button.
     */
    @Input() public buttonEmbedded: boolean = false;

    /**
     * Boolean to tell the button to use the button service.
     */
    @Input() public hasMultipleApiCalls: boolean = false;

    /**
     * Boolean to tell the button to not cancel the loading state.
     */
    @Input() public handleLoadingStateExternally: boolean = false;

    /**
     * Call back function.
     */
    @Input() public callBack: (...args: any[]) => void;

    /**
     * Where we are in state of loading.
     */
    public isLoading: boolean = false;

    constructor(
        private loadingService: LoadingService,
        private logService: LogService
    ) {
        super();

        this.loadingService.loadingButtonEmitter
            .pipe(takeUntil(this.destroyed$))
            .subscribe({
                next: (response: any): void => {
                    if (typeof (response) === 'boolean') {
                        this.isLoading = response;
                    } else {
                        this.isLoading = response.id === this.buttonId ? response.state : false;
                    }
                },
                error: (error: ErrorResponseModel): void => {
                    this.logService.warn(
                        'LoadingButtonComponent:loadingButtonEmitter - Failed to subscribe to loading button emitter',
                        error.message
                    );
                }
            });
    }

    /**
     * Set loading state when loading is clicked, trigger any callbacks.
     */
    public clickedButton(): void {
        this.isLoading = true;

        if (this.callBack) {
            // Callbacks use .bind(this) so we can't run isObservable on this.callBack directly, as it is bound we have no context.
            // However, calling the apply function calls it for you and leaves us with an unbound function we can check
            // isObservable on, then we just need to subscribe to the already called unboundFunction if it is Observable.
            const unboundFunction = this.callBack.apply(this.callBack);
            if (unboundFunction && isObservable(unboundFunction)) {
                unboundFunction
                    .pipe(takeUntil(this.destroyed$))
                    .subscribe({
                        next: (): void => {
                            this.callBackCompleteHandler();
                        },
                        error: () => this.loadingService.loadingButtonEmitter.next({ id: this.buttonId, state: false })
                    });
            } else {
                this.callBackCompleteHandler();
            }
        } else if (this.callBack && this.hasMultipleApiCalls) {
            this.logService.warn(
                'LoadingButtonComponent:clickedButton - Dual trigger',
                'You are using both a callBack and the function for multiple api calls'
            );
        } else if (this.hasMultipleApiCalls) {
            this.loadingService.loadingButtonEmitter.next({ id: this.buttonId, state: false });
        } else {
            this.logService.warn(
                'LoadingButtonComponent:clickedButton - No Callback set',
                'You must be using (click) on the loading button element.'
            );
        }
    }

    /**
     * Callback helper function to message console or clear loading button state.
     * @private
     */
    private callBackCompleteHandler(): void {
        if (this.handleLoadingStateExternally) {
            this.logService.debug(
                'LoadingButtonComponent:clickedButton - Handle loading state externally',
                'Waiting for external component to cancel loading state'
            );
        } else {
            this.loadingService.loadingButtonEmitter.next({ id: this.buttonId, state: false });
        }
    }
}
